<template>
  <article class="adoc-section" :id="section.id">
    <header class="adoc-section__hero">
      <div class="adoc-section__hero-icon">
        <v-icon :icon="section.icon" size="24" />
      </div>
      <div class="adoc-section__hero-copy">
        <h1 class="adoc-section__title">{{ section.title }}</h1>
        <ApiDocsRichText
          v-if="section.description"
          :html="section.description"
          class="adoc-section__desc"
        />
      </div>
    </header>

    <div v-if="section.endpoint" class="adoc-endpoint-card">
      <div class="adoc-endpoint-card__row">
        <span
          class="adoc-method"
          :class="`adoc-method--${methodClass}`"
        >
          {{ section.endpoint.method }}
        </span>
        <code class="adoc-endpoint-card__path">{{ section.endpoint.path }}</code>
        <button
          type="button"
          class="adoc-endpoint-card__try"
          :disabled="!canTest"
          :title="testHint"
          @click="testOpen = true"
        >
          <v-icon icon="mdi-play-circle-outline" size="16" />
          Try it out
        </button>
      </div>

      <div v-if="section.scopeNote" class="adoc-endpoint-card__scope">
        <span class="adoc-scope-chip">{{ section.scopeNote.scope }}</span>
        <ApiDocsRichText :html="section.scopeNote.html" tag="p" />
      </div>
    </div>

    <ApiDocsContactUsProductExample v-if="section.id === 'create-lead'" />

    <template v-for="block in contentBlocks" :key="block.key">
      <h3
        v-if="block.kind === 'heading'"
        class="adoc-subheading"
      >
        {{ block.text }}
      </h3>

      <ApiDocsRichText
        v-else-if="block.kind === 'paragraph'"
        :html="block.html"
        tag="p"
        content-class="adoc-paragraph"
      />

      <div
        v-else-if="block.kind === 'callout'"
        class="adoc-tip"
        :class="`adoc-tip--${block.variant}`"
      >
        <div class="adoc-tip__icon-wrap">
          <v-icon :icon="tipIcon(block.variant)" size="18" />
        </div>
        <div class="adoc-tip__body">
          <p class="adoc-tip__label">{{ tipLabel(block.variant) }}</p>
          <ApiDocsRichText :html="block.html" />
        </div>
        <v-icon icon="mdi-chevron-right" size="18" class="adoc-tip__chevron" />
      </div>

      <div v-else-if="block.kind === 'table'" class="adoc-table-card">
        <table class="adoc-table">
          <thead v-if="block.header">
            <tr>
              <th v-for="(cell, index) in block.header" :key="`h-${index}`">
                <ApiDocsRichText :html="cell.html" tag="span" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in block.rows" :key="`r-${rowIndex}`">
              <td v-for="(cell, cellIndex) in row" :key="`c-${cellIndex}`">
                <span
                  v-if="cell.required"
                  class="adoc-required"
                  :class="`adoc-required--${cell.required}`"
                >
                  {{ cell.required }}
                </span>
                <ApiDocsRichText :html="cell.html" tag="span" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="block.kind === 'code'" class="adoc-code-card">
        <div class="adoc-code-card__head">
          <div class="adoc-code-card__title">
            <v-icon icon="mdi-code-tags" size="16" />
            <span>{{ codeTitle(block) }}</span>
          </div>
          <button type="button" class="adoc-code-card__copy" @click="copyCode(block.code)">
            <v-icon size="14">{{ copiedCode === block.code ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            Copy
          </button>
        </div>
        <pre class="adoc-code-card__pre"><code>{{ block.code }}</code></pre>
      </div>

      <ul v-else-if="block.kind === 'list'" class="adoc-list">
        <li v-for="(item, index) in block.items" :key="index">
          <ApiDocsRichText :html="item" tag="span" />
        </li>
      </ul>
    </template>

    <!-- One per section, outside the content loop -- placing it inside would
         mount a dialog per rendered code block. -->
    <ApiDocsTestDialog
      v-model="testOpen"
      :endpoint="testEndpoint"
      :base-url="baseUrl"
      :api-key="apiKey"
    />
  </article>
</template>

<script setup>
import { computed, ref } from 'vue'
import ApiDocsRichText from '@/components/ApiKeys/docs/ApiDocsRichText.vue'
import ApiDocsContactUsProductExample from '@/components/ApiKeys/docs/ApiDocsContactUsProductExample.vue'
import ApiDocsTestDialog from '@/components/ApiKeys/ApiDocsTestDialog.vue'

defineOptions({ name: 'ApiDocsEndpointSection' })

const props = defineProps({
  section: { type: Object, required: true },
  apiKey: { type: String, default: '' },
  baseUrl: { type: String, default: '' },
})

const testOpen = ref(false)

// The tester needs a request body to start from, which the parser only
// produces when the docs carry a curl example -- plus the organisation key it
// sends with the call. Anything missing disables the button with a reason
// rather than opening a dialog that cannot work.
// The request is performed by the portal's backend, which holds the OAuth
// secret -- a browser cannot, since anyone with developer tools would then
// have credentials that place real, billed calls. So the only thing needed
// here is an endpoint with a runnable example to start from.
const canTest = computed(() => Boolean(props.section.endpoint?.testable))

const testHint = computed(() =>
  props.section.endpoint?.testable
    ? 'Send a real request — signed by your organisation, run from the server'
    : props.section.endpoint?.notTestableReason || 'Not runnable from here yet',
)

const testEndpoint = computed(() => ({
  method: props.section.endpoint?.method ?? 'POST',
  path: props.section.endpoint?.path ?? '',
  description: props.section.title ?? props.section.endpoint?.path ?? '',
  requestBody: props.section.endpoint?.requestBody || '{}',
}))

const emit = defineEmits(['copied', 'copy-error'])

const copiedCode = ref('')
let copyTimer = null

const methodClass = computed(() =>
  String(props.section.endpoint?.method ?? 'get').toLowerCase(),
)

const skipKinds = new Set(['heading', 'endpoint', 'scope-note'])

const contentBlocks = computed(() => {
  const blocks = props.section.blocks || []
  const headingId = blocks.find((b) => b.kind === 'heading')?.id
  let passedHero = false

  return blocks.filter((block) => {
    if (block.kind === 'heading' && block.level <= 2) {
      passedHero = true
      return false
    }
    if (!passedHero) return false
    if (skipKinds.has(block.kind)) return false
    if (block.kind === 'paragraph' && block.html === props.section.description) {
      return false
    }
    return true
  })
})

function tipIcon(variant) {
  if (variant === 'danger') return 'mdi-alert-circle-outline'
  if (variant === 'warn') return 'mdi-lightbulb-on-outline'
  return 'mdi-information-outline'
}

function tipLabel(variant) {
  if (variant === 'danger') return 'Important'
  if (variant === 'warn') return 'Best practice'
  return 'Note'
}

function codeTitle(block) {
  const index = contentBlocks.value.indexOf(block)
  const prev = contentBlocks.value[index - 1]
  if (prev?.kind === 'heading') return prev.text
  return 'Example request'
}

async function copyCode(code) {
  try {
    await navigator.clipboard.writeText(code)
    copiedCode.value = code
    emit('copied')
    if (copyTimer) window.clearTimeout(copyTimer)
    copyTimer = window.setTimeout(() => {
      copiedCode.value = ''
    }, 2000)
  } catch {
    emit('copy-error')
  }
}
</script>
